"use client";

import CircularRectTextButton, {
    CircularRectTextButtonProps,
} from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { ASSETS_URL } from "@/config/deployConstants";
import { useThemeContextSelector } from "@/utils/theme";
import { themeColors } from "@/utils/themeColors";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useState } from "react";
import { AuthEvent } from "../../AuthPopup/utils/constants";
import { useAuthSlideContextSelector } from "../../AuthSlide/hook/useAuthSlideContext";
import { socialSignIn } from "../../utils";
import { borderRadius } from "@/utils/borderRadius";

const GenericAuthButton = ({ children, sx, ...props }: CircularRectTextButtonProps) => {
    return (
        <CircularRectTextButton
            type="button"
            className="secondaryTextBold1"
            sx={{
                backgroundColor: "transparent",
                borderRadius: "4rem",
                fontWeight: "600",
                fontSize: "1.2rem",
                border: `1px solid ${themeColors.neutral1}`,
                gap: "1rem",
                "&:hover": {
                    background: themeColors.neutralBlack,
                    color: themeColors.neutralWhite,
                },
                "&:active": { transform: "scale(0.99)" },
                width: "5rem",
                height: "5rem",
                padding: "1rem",
                ...sx,
            }}
            {...props}>
            {children}
        </CircularRectTextButton>
    );
};

export const AuthGoogleButton = ({ label, ...props }: { label?: string } & CircularRectTextButtonProps) => {
    return (
        <GenericAuthButton tooltip={label ? undefined : "Continue with Google"} {...props}>
            <Image
                src={`${ASSETS_URL}/images/google-icon.svg`}
                alt="continue with google"
                width={19}
                height={20}
                style={{ objectFit: "contain" }}
            />
            {label}
        </GenericAuthButton>
    );
};

export const AuthCleverButton = ({ ...props }: CircularRectTextButtonProps) => {
    return (
        <GenericAuthButton tooltip="Continue with Clever" {...props}>
            <Image
                src={`${ASSETS_URL}/images/clever-icon.svg`}
                alt="continue with clever"
                width={19}
                height={20}
                style={{ objectFit: "contain" }}
            />
        </GenericAuthButton>
    );
};

export const AuthClassLinkButton = ({ ...props }: CircularRectTextButtonProps) => {
    return (
        <GenericAuthButton tooltip="Continue with ClassLink" {...props}>
            <Image
                src={`${ASSETS_URL}/images/classlink-icon.png`}
                alt="continue with classlink"
                width={20}
                height={20}
                style={{ objectFit: "contain" }}
            />
        </GenericAuthButton>
    );
};

export const AuthMicrosoftButton = ({ ...props }: CircularRectTextButtonProps) => {
    return (
        <GenericAuthButton tooltip="Continue with Microsoft" {...props}>
            <Image
                src={`${ASSETS_URL}/images/microsoft-icon.svg`}
                alt="continue with microsoft"
                width={20}
                height={20}
                style={{ objectFit: "contain" }}
            />
        </GenericAuthButton>
    );
};

export const AuthAppleButton = ({ label, ...props }: { label?: string } & CircularRectTextButtonProps) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const [isHover, setIsHover] = useState(false);

    return (
        <GenericAuthButton
            tooltip={label ? undefined : "Continue with Apple"}
            onMouseEnter={() => {
                setIsHover(true);
            }}
            onMouseLeave={() => {
                setIsHover(false);
            }}
            {...props}>
            <Image
                src={
                    isDarkMode
                        ? isHover
                            ? `${ASSETS_URL}/images/apple-icon.svg`
                            : `${ASSETS_URL}/images/apple-icon-white.svg`
                        : isHover
                          ? `${ASSETS_URL}/images/apple-icon-white.svg`
                          : `${ASSETS_URL}/images/apple-icon.svg`
                }
                alt="continue with apple"
                width={25}
                height={25}
                style={{ objectFit: "contain" }}
            />
            {label}
        </GenericAuthButton>
    );
};

export const AuthEmailButton = ({
    showText = false,
    ...props
}: {
    label?: string;
    showText?: boolean;
} & CircularRectTextButtonProps) => {
    return (
        <GenericAuthButton
            tooltip={showText ? undefined : "Continue with Email"}
            sx={{
                ...(showText && {
                    width: "100%",
                    height: "4rem",
                    borderRadius: borderRadius.shortInput,
                    padding: "2.5rem",
                }),
            }}
            {...props}>
            {showText ? (
                <span className="bodyBold2">Continue with Email</span>
            ) : (
                <Image
                    src={`${ASSETS_URL}/images/email-icon.svg`}
                    alt="continue with email"
                    width={27}
                    height={28}
                    style={{ objectFit: "contain" }}
                />
            )}
        </GenericAuthButton>
    );
};

export const AuthSignupSocialBtns = ({
    event,
    isUnder13,
    hideAppleFb,
    showEmailBtn = false,
    style,
}: {
    event: AuthEvent;
    isUnder13?: boolean;
    hideAppleFb?: boolean;
    showEmailBtn?: boolean;
    style?: React.CSSProperties;
}) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const openAuthSlide = useAuthSlideContextSelector(state => state.openAuthSlide);

    return (
        <FlexColumn style={{ ...style }}>
            <p className="bodyBold2" style={{ marginBottom: "1rem" }}>
                Continue with
            </p>
            <FlexRowAlignCenter style={{ justifyContent: "space-between", flexWrap: "wrap", gap: "2rem" }}>
                <AuthGoogleButton onClick={() => socialSignIn({ provider: "Google", event, pathname, searchParams })} />
                {!isUnder13 && !hideAppleFb && (
                    <AuthAppleButton
                        onClick={() => socialSignIn({ provider: "Apple", event, pathname, searchParams })}
                    />
                )}
                {/* <AuthMicrosoftButton
                    onClick={() => socialSignIn({ provider: { custom: "Microsoft" }, event, pathname, searchParams })}
                /> */}
                <AuthCleverButton
                    onClick={() => socialSignIn({ provider: { custom: "Clever" }, event, pathname, searchParams })}
                />
                <AuthClassLinkButton
                    onClick={() => socialSignIn({ provider: { custom: "ClassLink" }, event, pathname, searchParams })}
                />
                {showEmailBtn && (
                    <AuthEmailButton
                        onClick={() => {
                            openAuthSlide({ showSignupPage: true, event });
                        }}
                    />
                )}
            </FlexRowAlignCenter>
        </FlexColumn>
    );
};
